import type { LocalizedString } from 'types/context';

/**
 * The one place a `LocalizedString` JSONB value (`{ default, translations }`) becomes display
 * text. Four independent copies of this had accumulated — `useEligibleApproaches`,
 * `useTemplateApproaches`, `buildApproachTree` and the Studio drawer's channel label — each
 * with a different fallback order, so the same channel could be labelled differently in two
 * places. Columns of this shape are also sometimes a bare string (older rows, and routes that
 * pass a display name straight through), so the string branch is part of the contract.
 *
 * Precedence: the requested locale, then `en-US`, then `default`, then the caller's fallback.
 * `locale` is optional so callers with no `i18n` in scope get the `en-US`/`default` behaviour
 * the previous copies had.
 */
export function resolveLocalizedString(
  // Structural rather than `LocalizedString` itself: `LocalizedString` requires `default`, but
  // rows written before the column was normalized (and routes that pass a display name straight
  // through) legitimately arrive without it. Narrowing here would only push the cast to callers.
  value: Partial<LocalizedString> | string | null | undefined,
  fallback = '',
  locale?: string,
): string {
  if (!value) return fallback;
  if (typeof value === 'string') return value || fallback;
  return (
    (locale ? value.translations?.[locale] : undefined) ??
    value.translations?.['en-US'] ??
    value.default ??
    fallback
  );
}

export default resolveLocalizedString;
